import { Buttons, clampInput, has, type PlayerInput } from "./input.ts";
import type { MatchState } from "./state.ts";
import { updateCage } from "./step/cage.ts";
import { resolveBodies } from "./step/collision.ts";
import type { SimEvent } from "./step/events.ts";
import { recordPast } from "./step/lag.ts";
import { checkEliminations, checkMatchEnd } from "./step/match.ts";
import { applyMovement, armIntact, integrate, poseLimbs } from "./step/movement.ts";
import { updateStamina } from "./step/stamina.ts";
import { updateStrikeIntent, updateStrikes } from "./step/strikes.ts";

export type { HitEvent, SimEvent } from "./step/events.ts";

export interface StepOptions {
  /** Client prediction: move limbs but never resolve impacts. */
  predictOnly?: boolean;
}

/**
 * Advances the match by one tick. `inputs[i]` is fighter i's input for this
 * tick or null to repeat the last one. Pure with respect to everything but
 * `state`, which it mutates in place; returns the events of this tick.
 */
export function step(
  state: MatchState,
  inputs: readonly (PlayerInput | null)[],
  options: StepOptions = {},
): SimEvent[] {
  const events: SimEvent[] = [];
  if (state.phase === "ended") return events;
  const predictOnly = options.predictOnly === true;

  // 1. Intent: look, movement, duck, step, guard, strikes.
  for (let i = 0; i < state.fighters.length; i += 1) {
    const f = state.fighters[i];
    if (!f?.alive) continue;
    const raw = inputs[i];
    const input = raw ? clampInput(raw) : f.lastInput;
    applyMovement(state, f, input);
    f.guard = has(input, Buttons.GUARD) && (armIntact(f, 0) || armIntact(f, 1));
    updateStrikeIntent(state, f, input);
    f.lastInput = input;
  }

  // 2. Integrate and resolve bodies.
  for (const f of state.fighters) if (f.alive) integrate(f);
  resolveBodies(state, events);

  // 3. Strikes move limbs and sweep; free limbs pose; stamina.
  for (const f of state.fighters) {
    if (!f.alive) continue;
    updateStrikes(state, f, events, predictOnly);
  }
  for (const f of state.fighters) {
    if (!f.alive) continue;
    poseLimbs(f);
    updateStamina(
      f,
      f.strikeArm !== null || f.strikeLeg !== null || f.chargeArm !== null || f.chargeLeg !== null,
    );
    f.prevButtons = f.lastInput.buttons;
  }

  // 4. Clock, cage and outcome. After this step the state represents time `tick`.
  state.tick += 1;
  recordPast(state);
  if (state.phase === "countdown" && state.tick >= state.fightStartTick) {
    state.phase = "fight";
  }
  if (state.phase === "fight") {
    updateCage(state, events);
    state.clockTicks -= 1;
    checkEliminations(state, events);
    checkMatchEnd(state, events);
  }
  return events;
}
